import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, User, Lock, GraduationCap, ShoppingCart, ChevronRight, FileSignature, Download, Award } from "lucide-react";
import { PersonalProfile, Card, Field, fmtDateTime } from "@/components/PersonalProfile";
import { toast } from "sonner";
import { STATE_META, fmtDate } from "@/components/EnrollmentsDialog";
import { MySessions } from "@/components/AccessWidgets";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);

export default function Account() {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isStudent = user?.role === "student";

  const SECTIONS = [
    { key: "dados", label: "Dados pessoais", icon: User },
    { key: "seguranca", label: "Dados de acesso e segurança", icon: Lock },
    ...(isStudent ? [
      { key: "matriculas", label: "Matrículas", icon: GraduationCap },
      { key: "compras", label: "Compras", icon: ShoppingCart },
      { key: "certificados", label: "Meus certificados", icon: Award },
      { key: "contrato", label: "Meu contrato", icon: FileSignature },
    ] : []),
  ];
  // seção pela âncora da URL (#senha continua funcionando, vindo de "Alterar senha")
  const hash = location.hash.replace("#", "");
  const section = hash === "senha" ? "seguranca" : SECTIONS.some((s) => s.key === hash) ? hash : "dados";
  const go = (key) => navigate({ hash: key }, { replace: true });
  const current = SECTIONS.find((s) => s.key === section);

  return (
    <div className="max-w-6xl mx-auto w-full animate-fade-up">
      <div className="mb-5">
        <h1 className="font-display text-2xl font-bold text-slate-900">Minha conta</h1>
        <p className="text-slate-500 text-sm mt-1">Gerencie as informações da sua conta e seus dados pessoais.</p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6 items-start">
        {/* menu de seções */}
        <nav className="bg-white border border-slate-200 rounded-2xl p-2 lg:sticky lg:top-24 flex lg:block gap-1 overflow-x-auto" data-testid="account-sections">
          {SECTIONS.map((s) => {
            const active = s.key === section;
            return (
              <button key={s.key} onClick={() => go(s.key)} data-testid={`account-section-${s.key}`}
                className={`shrink-0 lg:w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left text-[15px] transition-colors ${
                  active ? "text-[#1E9E2A] font-semibold bg-green-50" : "text-slate-600 hover:bg-slate-50"}`}>
                <s.icon className="h-5 w-5 shrink-0" />
                <span className="flex-1 whitespace-nowrap lg:whitespace-normal lg:leading-snug">{s.label}</span>
                <ChevronRight className="h-4 w-4 shrink-0 hidden lg:block" />
              </button>
            );
          })}
        </nav>

        <div className="space-y-6 min-w-0">
          <div className="flex items-center gap-3">
            {current && <span className="h-10 w-10 rounded-xl grid place-items-center bg-green-50 text-[#1E9E2A]"><current.icon className="h-5 w-5" /></span>}
            <h2 className="font-display text-2xl font-bold text-slate-900">{current?.label}</h2>
          </div>
          {section === "dados" && <PersonalSection user={user} setUser={setUser} isStudent={isStudent} />}
          {section === "seguranca" && <SecuritySection user={user} />}
          {section === "matriculas" && <EnrollmentsSection />}
          {section === "compras" && <OrdersSection />}
          {section === "certificados" && <CertificatesSection />}
          {section === "contrato" && <ContractSection />}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Dados pessoais ----------------------------- */
function PersonalSection({ user, setUser, isStudent }) {
  const [details, setDetails] = useState(null);
  useEffect(() => {
    api.get("/me/details").then((r) => setDetails(r.data)).catch(() => setDetails({ person: {} }));
  }, []);
  const upload = async (kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUser((await api.post(`/me/${kind}`, fd)).data);
  };
  return (
    <PersonalProfile
      subject={user}
      details={details}
      isStudent={isStudent}
      isInstructor={user?.role === "instructor"}
      onSave={async (body) => setUser((await api.put("/me", body)).data)}
      onUpload={upload}
      onRemove={async (kind) => setUser((await api.delete(`/me/${kind}`)).data)}
    />
  );
}

/* ------------------------ Dados de acesso e segurança ------------------------ */
export function SecuritySection({ user }) {
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) return toast.error("A confirmação não confere com a nova senha.");
    setSaving(true);
    try {
      await api.post("/me/password", { current_password: pw.current_password, new_password: pw.new_password });
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Senha alterada!");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <Card title="Acesso">
        <Field label="E-mail de login"><Input value={user?.email || ""} readOnly /></Field>
        <p className="text-xs text-slate-400 mt-2">O e-mail é o seu login. Para alterá-lo, fale com a administração.</p>
      </Card>
      <form id="senha" onSubmit={save}>
        <Card title="Alterar senha">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Senha atual"><Input type="password" autoComplete="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required /></Field>
            <Field label="Nova senha"><Input type="password" autoComplete="new-password" minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required /></Field>
            <Field label="Confirmar nova senha"><Input type="password" autoComplete="new-password" minLength={8} value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} required /></Field>
          </div>
          <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
            <p className="text-xs text-slate-400">Mínimo de 8 caracteres. Esqueceu a senha atual? Saia e use “Esqueci minha senha”.</p>
            <Button type="submit" disabled={saving} className="text-white" style={{ background: "#232D4F" }} data-testid="account-password-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Alterar senha"}
            </Button>
          </div>
        </Card>
      </form>
      <MySessions />
    </>
  );
}

/* --------------------------------- Matrículas -------------------------------- */
function EnrollmentsSection() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/me/enrollments").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);
  if (items === null) return <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!items.length) return <Card><p className="text-sm text-slate-500">Você ainda não está matriculado(a) em nenhum curso.</p></Card>;
  return (
    <Card>
      <div className="divide-y divide-slate-100">
        {items.map((i) => {
          const meta = STATE_META[i.state] || STATE_META.ativa;
          return (
            <div key={i.course_id} className="py-3 flex items-center gap-3 flex-wrap">
              <GraduationCap className="h-5 w-5 text-[#1E9E2A]" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800">{i.title}</div>
                <div className="text-xs text-slate-400">
                  {i.expires_at ? `Acesso até ${fmtDate(i.expires_at)}` : "Acesso sem prazo"}
                  {i.starts_at && ` · a partir de ${fmtDate(i.starts_at)}`}
                  {i.groups?.length > 0 && ` · Turma: ${i.groups.join(", ")}`}
                </div>
              </div>
              {i.completed && <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800">Concluído{i.completed_at ? ` em ${fmtDate(i.completed_at)}` : ""}</span>}
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${meta.cls}`}>{meta.label}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ---------------------------------- Compras ---------------------------------- */
const ORDER_STATUS = { approved: ["Aprovado", "bg-green-50 text-green-700"], pending: ["Pendente", "bg-amber-50 text-amber-700"],
  rejected: ["Recusado", "bg-red-50 text-red-700"], cancelled: ["Cancelado", "bg-slate-100 text-slate-600"] };

export function OrdersSection({ url = "/me/orders", admin = false }) {
  const [orders, setOrders] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get(url).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  useEffect(() => { load(); }, [url]); // eslint-disable-line react-hooks/exhaustive-deps

  const aprovar = async (o) => {
    if (!(await confirmar(`Aprovar manualmente a compra de "${o.course}" (${Number(o.amount || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })})?\n\nO curso é liberado para o aluno agora e a venda entra como paga no financeiro.`))) return;
    setBusy(o.id);
    try { await api.post(`/admin/finance/orders/${o.id}/approve`); toast.success("Compra aprovada e curso liberado."); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  };
  const baixarNota = async (o) => {
    try { const r = await api.get(`/me/orders/${o.id}/nfse`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error("A nota fiscal ainda não está disponível. Tente de novo em alguns minutos."); }
  };
  const excluir = async (o) => {
    if (!(await confirmar(`Excluir o pedido de "${o.course}"?\n\nIsso apaga o pedido da lista. Se o aluno já pagou pelo link, não exclua: aprove o pedido.`))) return;
    setBusy(o.id);
    try { await api.delete(`/admin/finance/orders/${o.id}`); toast.success("Pedido excluído."); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  };

  if (orders === null) return <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!orders.length) return <Card><p className="text-sm text-slate-500">Nenhuma compra realizada.</p></Card>;
  return (
    <Card>
      <div className="divide-y divide-slate-100">
        {orders.map((o) => {
          const [label, cls] = ORDER_STATUS[o.status] || [o.status, "bg-slate-100 text-slate-600"];
          return (
            <div key={o.id} className="py-3 flex items-center gap-3 flex-wrap">
              <ShoppingCart className="h-5 w-5 text-[#232D4F]" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800">{o.course}</div>
                <div className="text-xs text-slate-400">{fmtDateTime(o.created_at)}</div>
              </div>
              <span className="text-sm font-semibold text-slate-700">{Number(o.amount || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</span>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>
              {!admin && o.nfse && <Button size="sm" variant="outline" onClick={() => baixarNota(o)} data-testid={`order-nfse-${o.id}`}><Download className="h-4 w-4 mr-1" /> Nota fiscal{o.nfse_numero ? ` nº ${o.nfse_numero}` : ""}</Button>}
              {admin && o.status !== "approved" && (
                <div className="flex items-center gap-2">
                  <Button size="sm" disabled={busy === o.id} onClick={() => aprovar(o)} className="text-white font-semibold" style={{ background: "#1E9E2A" }} data-testid={`order-approve-${o.id}`}>
                    {busy === o.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aprovar"}
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy === o.id} onClick={() => excluir(o)} className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" data-testid={`order-delete-${o.id}`}>Excluir</Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}


/* ---------------------------------- Contrato ---------------------------------- */
function CertificatesSection() {
  const [rows, setRows] = useState(null);
  const [pend, setPend] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/me/certificates").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/me/certificates/disponiveis").then((r) => setPend(r.data)).catch(() => {});
  }, []);
  if (rows === null) return <div className="py-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  return (
    <Card>
      {pend.length > 0 && (
        <div className="mb-4 space-y-2">
          {pend.map((c) => (
            <div key={c.course_id} className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex items-center gap-3 flex-wrap">
              <Award className="h-5 w-5 text-[#1E9E2A]" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800">{c.course_title}</div>
                <div className="text-xs text-slate-500">{c.pode_emitir ? "Curso concluído: seu certificado está pronto para emitir." : `Curso concluído. O certificado fica disponível a partir de ${fmtDate(c.liberado_em)}.`}</div>
              </div>
              {c.pode_emitir && <Button size="sm" className="bg-[#1E9E2A] hover:bg-green-700 text-white" onClick={() => navigate(`/app/certificado/${c.course_id}`)}>Emitir certificado</Button>}
            </div>
          ))}
        </div>
      )}
      {rows.length === 0 && pend.length === 0 ? <p className="text-sm text-slate-500">Você ainda não tem certificados. Eles aparecem aqui quando você conclui um curso.</p> : rows.length === 0 ? null : (
        <div className="divide-y divide-slate-100">
          {rows.map((c) => (
            <div key={c.code} className="py-3 flex items-center gap-3 flex-wrap">
              <Award className="h-5 w-5 text-[#1E9E2A]" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-800">{c.course_title}</div>
                <div className="text-xs text-slate-400">Concluído em {fmtDate((c.issued_at || "").slice(0, 10))} · Código {c.code}{c.legacy ? " · emitido na plataforma anterior" : ""}{c.revoked ? " · cancelado" : ""}</div>
              </div>
              {!c.revoked && <Button size="sm" variant="outline" onClick={() => navigate(`/app/certificado/${c.course_id}`)}>Abrir / imprimir</Button>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function ContractSection() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/me/contract").then((r) => setC(r.data)).catch(() => setC(false)); }, []);
  const baixar = async () => {
    try {
      const r = await api.get("/me/contract/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `contrato-assinado-${c.signed.code}.pdf`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Não foi possível baixar agora."); }
  };
  if (c === null) return <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (!c || !c.signed) return <Card><p className="text-sm text-slate-500">{c && c.required ? "Você ainda não assinou o contrato." : "Nenhum contrato assinado por aqui ainda."}</p></Card>;
  return (
    <Card>
      <div className="flex items-start gap-4 flex-wrap">
        <FileSignature className="h-8 w-8 text-[#1E9E2A] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-800">Contrato assinado</div>
          <div className="text-sm text-slate-500 mt-0.5">Versão {c.signed.version} · {fmtDateTime(c.signed.signed_at)}</div>
          <div className="text-xs text-slate-400 mt-1">Código de verificação: <span className="font-mono text-slate-600">{c.signed.code}</span></div>
        </div>
        <Button variant="outline" onClick={baixar} data-testid="account-contract-download"><Download className="h-4 w-4 mr-1.5" /> Baixar cópia (PDF)</Button>
      </div>
    </Card>
  );
}
